import Route from '@ember/routing/route';
import { service } from '@ember/service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type RouterService from '@ember/routing/router';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import type SessionContextService from '../../../services/session-context';
import type FinancialInstitutionModel from 'tio-common/models/financial-institution';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type ReimaginedViewService from 'tio-employee/services/reimagined-view';
import type { ReimaginedDocumentCenterModel } from 'tio-employee/components/reimagined/production/document-center';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type ObservabilityUploadRouteModel = Resolved<
  ReturnType<AuthenticatedObservabilityUploadRoute['model']>
>;

export default class AuthenticatedObservabilityUploadRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare remoteEventLogger: RemoteEventLoggerService;
  @service declare reimaginedView: ReimaginedViewService;

  async model() {
    const person = this.sessionContext.user.person;

    const institutionsQuery = {
      filter: {
        isActive: true,
        hasObservabilityInstructions: true,
      },
    };

    const financialInstitutionsResult = await this.store.query<FinancialInstitutionModel>(
      'financial-institution',
      institutionsQuery
    );

    const financialInstitutions = [...financialInstitutionsResult].sort(
      (a: FinancialInstitutionModel, b: FinancialInstitutionModel) => a.name?.localeCompare(b.name)
    );

    this.remoteEventLogger.logObservabilityEvent({
      eventName: 'INITIALIZE',
      component: 'AuthenticatedObservabilityUploadRoute',
    });

    // When reimagined view is enabled, fetch document data for the Document Center
    if (this.reimaginedView.isEnabled) {
      const personId = person.id;
      let observableDocuments: ObservableDocumentModel[] = [];

      if (personId) {
        try {
          const results = await this.store.query<ObservableDocumentModel>('observable-document', {
            filter: { person: personId },
            include: 'observable-asset',
            sort: '-created-at',
            page: { limit: 25 },
          });
          observableDocuments = [...results];
        } catch {
          // Fail gracefully — document center will show empty state
        }
      }

      const reimagined: ReimaginedDocumentCenterModel = {
        observableDocuments,
      };

      return { person, financialInstitutions, reimagined };
    }

    return { person, financialInstitutions };
  }
}
